<template>
    <div class="grid" ref="root">
        <loader v-if="is('loading')" :inline="inlineLoader"></loader>
        <div v-if="!is('loading')">
            <slot name="grid-header" v-bind:data="response"></slot>
        </div>
        <div class="grid-header pb-2">
            <div class="search">
                <template v-if="showSearch">
                    <input type="text" class="form-control" :placeholder="t('grid-search_placeholder')" @keydown.enter="search = searchField" v-model="searchField" />
                    <span v-if="search" class="results">{{ total === 1 ? t('grid-showing_results', [total]) : t('grid-showing_results_plural', [total]) }}</span>
                </template>
            </div>
            <div class="grid-actions">
                <slot class="grid-actions" name="grid-actions"></slot>
            </div>
        </div>
        <div class="table-wrapper">
            <div class="table-responsive" v-if="!isUsingList">
                <table class="table table-stripped" v-bind:style="tableStyle">
                    <slot name="rows" v-bind:data="data" v-bind:sort="sort" v-bind:sortingBy="sortingBy"></slot>
                </table>
            </div>
        </div>

        <div class="list" v-if="isUsingList">
            <slot name="list" v-bind:data="data" v-bind:sort="sort" v-bind:sortingBy="sortingBy"></slot>
        </div>
        <div>

        </div>

        <kv-pagination
            v-if="total > 0"
            :total="total"
            :current-page="currentPage"
            :items-per-page="itemsPerPage"
            :show-items-per-page="displayItemsPerPage"
            @page-change="setPage"
            @items-per-page-change="itemsPerPage = $event"
        />

    </div>
</template>
<style lang="scss">
.grid {
    .font-semibold {
        font-weight: 600;
    }

    .table {
        margin-bottom: 0;
    }

    .table td,
    .table th {
        padding: 0;
        line-height: normal;
    }

    .table td,
    .table th {
        text-align: start !important;
        padding: 0 12px;
    }

    .table thead th {
        line-height: 1;
        height: 50px;
    }

    .table tbody tr {
        &:hover td {
            background-color: #e5e7eb;
        }

        td {
            background-color: #FBFBFB;
            height: 50px;
            vertical-align: middle;
            transition: all ease-in-out 0.3s;
            color: #4F4F4F;
        }

        .visible-on-hover {
            opacity: 0;
            transition: all ease-in-out 0.3s;
        }

        &:hover .visible-on-hover {
            opacity: 1;
        }


    }

    .table-wrapper {
        position: relative;
        overflow: hidden;
        border-radius: 12px;

        &:before {
            content: "";
            position: absolute;
            inset: 0;
            border: 1px solid #E5E7EB;
            pointer-events: none;
            border-radius: 12px;
        }
    }

    .table-responsive {
        border-radius: 16px;
        background: #FBFBFB;

        /* width */
        &::-webkit-scrollbar {
            height: 12px;
        }

        /* Track */
        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 10px;
        }

        /* Handle */
        &::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 10px;
            border: 2px solid #f1f1f1; // deixa o thumb mais “flutuante”
        }

        /* Handle on hover */
        &::-webkit-scrollbar-thumb:hover {
            background: #555;
        }
    }

    .kv-table-footer {
        display: flex;
        justify-content: space-between;
        margin-top: 10px;
        align-items: center;
        flex-wrap: wrap;

        .kv-table-items-per-page {
            font-size: 14px;
        }

        .kv-table-info {
            color: #9DA4AE;
            font-weight: 500;
        }
    }

    .kv-pagination {
        display: flex;
        justify-content: flex-end;
        gap: 5px;

        .popup-page-input {
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .outlined-container {
            display: flex;
            border: 1px solid #F3F4F6;
            align-items: center;
            padding: 5px;
            border-radius: 8px;
            gap: 5px;
        }

        .page {
            color: #888;
            font-weight: 500;
            width: 35px;
            height: 35px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            box-sizing: border-box;
            border-radius: 8px;
            cursor: pointer;
            transition: all ease-in-out 0.3s;

            &:hover {
                background-color: #F1FCF3;
            }

            &.active {
                background-color: #2AAA4C;
                color: #FFF;
            }
        }

        .kv-disabled {
            opacity: 0.5;
        }

        // 🔹 Ajustes para mobile
        @media (max-width: 600px) {
            justify-content: center;
            gap: 2px;
            // flex: 1;

            .outlined-container {
                padding: 3px;
                gap: 2px;
            }

            .page {
                width: 26px;
                height: 26px;
                font-size: 12px;
                border-radius: 6px;
            }

            .kv-before,
            .kv-right {
                width: 28px;
                height: 28px;
            }
        }
    }
}
</style>

<script>
import keviApi from "../../keviApi.js";
import Icon from "../icon/icon.vue";
import KvPagination from "../ui/kv-pagination.vue";

export default {
    name: "grid",
    components: {
        Icon,
        KvPagination
    },
    props: {
        source: String,
        filters: {
            type: Object,
            default: {},
        },
        name: {
            type: String,
            default: "grid",
        },
        showSearch: {
            type: Boolean,
            default: true,
        },
        quantityPerPage: {
            type: Number,
            default: 20,
        },
        displayItemsPerPage: {
            type: Boolean,
            default: false,
        },
        inlineLoader: {
            type: Boolean,
            default: false,
        },
    },
    data: function () {
        return {
            data: [],
            header: [],
            total: 0,
            pages: 1,
            response: {},
            currentPage: 1,
            sortingBy: {
                attr: null,
                method: null,
            },
            search: "",
            searchField: "",
            state: "loading",
            isMobile: false,
            itemsPerPage: this.quantityPerPage
        };
    },
    mounted: function () {
        this.isMounted = false;

        this.loadQueryString();
        this.update();
        this.isMobile = window.innerWidth <= 850;
        window.onresize = () => {
            this.isMobile = window.innerWidth <= 850;
        };

        this.$nextTick(() => {
            this.isMounted = true;
        });
    },
    computed: {
        hasListSlot() {
            return this.$scopedSlots.list;
        },
        isUsingList() {
            return this.isMobile && this.hasListSlot;
        },
        tableStyle() {
            if (this.is("loading")) {
                return {
                    opacity: "0.3",
                };
            } else {
                return {};
            }
        },
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        is(state) {
            return this.state === state;
        },
        setState(state) {
            this.state = state;
        },
        update() {
            this.setState("loading");
            this.updateQueryString();
            const skipTotal = this.total > 0;
            let options = keviApi.getOptions(this.source, "GET", {
                page: this.currentPage,
                quantityPerPage: this.itemsPerPage,
                sort: this.sortingBy.attr,
                sortMethod: this.sortingBy.method,
                search: this.search,
                filters: this.filters,
                skipTotal: skipTotal ? 1 : 0,
            });

            options.before = (request) => {
                // Se já existir uma request pendente, cancela
                if (this.currentRequest) {
                    this.currentRequest.abort();
                }
                this.currentRequest = request; // guarda a requisição atual
            };

            this.$http(options).then(function (response) {
                if (response.status === 200) {
                    this.data = response.body.data;
                    this.response = response.body;
                    this.response["data"] = null;
                    if (!skipTotal) {
                        this.total = response.body.total;
                        this.pages = Math.ceil(
                            response.body.total / this.itemsPerPage,
                        );
                    }
                    this.$emit("loaded", {
                        data: this.data,
                        total: this.total,
                        response: this.response,
                    });
                    this.setState("list");
                } else {
                    this.setState("empty");
                }
            });
        },
        setPage(page) {
            if (page > 0 && page <= this.pages && page != this.currentPage) {
                this.currentPage = page;
                this.update();
                return true;
            }

            return false

        },
        loadQueryString() {
            let queryStringData = this.$route.query[this.name];
            if (queryStringData) {
                queryStringData = JSON.parse(queryStringData);
                this.currentPage = queryStringData.page;
                this.itemsPerPage = queryStringData.itemsPerPage;
                this.sortingBy = queryStringData.sortingBy;
                this.search = queryStringData.search;
                this.searchField = queryStringData.search;
            }
        },
        updateQueryString() {
            let oldQuery = JSON.parse(JSON.stringify(this.$route.query));
            let newQuery = JSON.parse(JSON.stringify(this.$route.query));
            newQuery[this.name] = JSON.stringify({
                page: this.currentPage,
                sortingBy: this.sortingBy,
                search: this.search,
                itemsPerPage: this.itemsPerPage
            });
            if (newQuery !== oldQuery) {
                this.$router.replace({ query: newQuery }).catch(() => { });
            }
        },
        sort(attr) {
            if (attr === this.sortingBy.attr && this.sortingBy.method == "desc") {
                this.sortingBy.method = null;
                this.sortingBy.attr = null;
            } else
                if (this.sortingBy.attr === attr) {
                    this.sortingBy.method =
                        this.sortingBy.method === "asc" ? "desc" : "asc";
                } else {
                    this.sortingBy.attr = attr;
                    this.sortingBy.method = "asc";
                }
            this.currentPage = 1;
            this.update();
            this.$emit("sort", this.sortingBy);
        },
    },
    watch: {
        itemsPerPage() {
            if (!this.isMounted) return;

            this.total = 0
            this.currentPage = 1
            this.update()
        },
        source() {
            this.total = 0;
            this.currentPage = 1;
            this.update();
        },
        search() {
            this.total = 0;
            this.currentPage = 1;
            this.update();
        },
        filters() {
            if (!this.isMounted) {
                this.update();
                return;
            }

            this.total = 0;
            this.currentPage = 1;
            this.update();
        },
    },
};
</script>
